import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import { RichTextHtml } from "../../RichTextHtml";
import type { ImageListingBlockItem, IImageListingBlockProps } from "./ImageListingBlock.props";

const ImageListingCardContent = ({
  horizontal,
  item,
}: {
  horizontal: boolean;
  item: ImageListingBlockItem;
}) => (
  <>
    <TootMediaFrame
      alt={item.image.alternateText}
      aspectRatio={horizontal ? "16 / 10" : "4 / 3"}
      className={cn(
        "rounded-none border-0 bg-[var(--color-toot-black)] [&_img]:brightness-[0.9] [&_img]:contrast-[1.08]",
        horizontal ? "h-full [&>div]:h-full" : "[&>div]:w-full",
      )}
      focalPoint={item.image.focalPoint ?? undefined}
      height={item.image.height ?? undefined}
      loading="lazy"
      sizes={item.image.sizes ?? (horizontal ? "16rem" : "(min-width: 1024px) 36vw, 92vw")}
      src={item.image.defaultSrc}
      srcSet={item.image.srcSet ?? undefined}
      width={item.image.width ?? undefined}
    />
    <div className="flex min-w-0 flex-1 flex-col gap-4 p-5 md:p-6">
      {item.badges?.length ? (
        <ul aria-label="Badges" className="flex list-none flex-wrap gap-2 p-0">
          {item.badges.map((badge, badgeIndex) => (
            <li
              className="w-fit rounded-[var(--radius-toot-panel)] border border-toot-grid-strong bg-toot-surface-raised/70 px-2.5 py-1 text-[0.67rem] font-bold uppercase tracking-[0.16em] text-toot-orange"
              key={`${badge.text}-${badgeIndex}`}
            >
              {badge.text}
            </li>
          ))}
        </ul>
      ) : null}
      <h3 className="text-xl font-black tracking-[-0.04em] text-toot-foreground">{item.heading}</h3>
      {item.subheading ? (
        <RichTextHtml
          className="max-w-3xl text-sm leading-6 text-toot-muted"
          html={item.subheading}
        />
      ) : null}
      {item.tags?.length ? (
        <ul className="flex list-none flex-wrap gap-2 p-0" aria-label="Image listing tags">
          {item.tags.map((tag, tagIndex) => (
            <li
              className="rounded-[var(--radius-toot-panel)] border border-toot-grid-strong bg-toot-surface-raised/60 px-2.5 py-1 text-xs font-semibold text-toot-muted"
              key={`${tag.tag}-${tagIndex}`}
            >
              {tag.tag}
            </li>
          ))}
        </ul>
      ) : null}
      {item.link?.text ? (
        <span className="mt-auto font-mono text-xs font-black uppercase tracking-[0.18em] text-toot-orange">
          {item.link.text} ↗
        </span>
      ) : null}
    </div>
  </>
);

const ImageListingCard = ({
  item,
  variant,
}: {
  item: ImageListingBlockItem;
  variant: IImageListingBlockProps["variant"];
}) => {
  const horizontal = variant === "horizontal-list";
  if (horizontal) {
    const Link = resolveTootPrimitive("Link");
    const content = (
      <article className="group/toot-card grid min-w-0 overflow-hidden rounded-[var(--toot-radius-card)] border border-toot-grid/90 bg-toot-surface-raised/65 text-[var(--toot-color-foreground)] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_55px_rgba(0,0,0,0.28)] transition-colors duration-300 hover:border-orange-400/55 md:grid-cols-[16rem_minmax(0,1fr)]">
        <ImageListingCardContent horizontal item={item} />
      </article>
    );

    return item.link?.href ? (
      <Link
        aria-label={item.link.ariaLabel ?? undefined}
        className="block rounded-[var(--toot-radius-card)] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/80"
        href={item.link.href}
        rel={item.link.target === "_blank" ? "noopener noreferrer" : undefined}
        target={item.link.target ?? undefined}
      >
        {content}
      </Link>
    ) : (
      content
    );
  }

  const Link = resolveTootPrimitive("Link");
  const content = (
    <article className="group/toot-card flex h-full min-w-0 flex-col overflow-hidden rounded-[var(--toot-radius-card)] border border-toot-grid/90 bg-toot-surface-raised/65 text-[var(--toot-color-foreground)] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_18px_55px_rgba(0,0,0,0.28)] transition-colors duration-300 hover:border-orange-400/55">
      <ImageListingCardContent horizontal={false} item={item} />
    </article>
  );

  return item.link?.href ? (
    <Link
      aria-label={item.link.ariaLabel ?? undefined}
      className="block h-full rounded-[var(--toot-radius-card)] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/80"
      href={item.link.href}
      rel={item.link.target === "_blank" ? "noopener noreferrer" : undefined}
      target={item.link.target ?? undefined}
    >
      {content}
    </Link>
  ) : (
    content
  );
};

export const ImageListingBlock = ({
  anchorId,
  buttons,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
  subheading,
  variant,
}: IImageListingBlockProps) => {
  if (!items?.length) {
    return null;
  }

  const header =
    eyebrow || heading || subheading ? (
      <TootHeading eyebrow={eyebrow} heading={heading} subheading={subheading} />
    ) : undefined;
  const actions = buttons?.length ? <TootActions actions={buttons} /> : undefined;

  return (
    <TootSectionShell
      actions={actions}
      className="space-y-6"
      header={header}
      id={anchorId ?? undefined}
      rootClassName={cn("image-listing-block", parentClass)}
      spacing={spacing ?? undefined}
      width="wide"
    >
      <div
        className={cn(
          "grid gap-4",
          variant === "horizontal-list"
            ? "grid-cols-1"
            : variant === "three-column"
              ? "md:grid-cols-2 xl:grid-cols-3"
              : "lg:grid-cols-2",
        )}
      >
        {items.map((item) => (
          <ImageListingCard
            item={item}
            key={`${item.heading}-${item.image.defaultSrc}`}
            variant={variant}
          />
        ))}
      </div>
    </TootSectionShell>
  );
};

ImageListingBlock.displayName = "Block::ImageListingBlock";
